import { produce } from 'immer';
import { NonUndefined } from 'react-hook-form';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';

import {
  FlowMessengerAction,
  FlowMessengerNodeSubflowReference,
  FlowMessengerNodeSubflowReturn,
  SubflowMessengerAction,
} from '../../flow.types';
import { MessengerFlow } from '../../hooks';

import { SubflowReferenceNode } from './subflowReferenceNodeUtils';

type Args = {
  mainFlow: MessengerFlow;
  subflow: NonUndefined<MessengerFlow['subflows']>[number];
};

export function updateSubflowReferencesInMainFlow({
  mainFlow,
  subflow,
}: Args): MessengerFlow {
  const allSubflowReturnNodes = getAllCurrentSubflowReturnNodes(
    subflow.actions,
  );

  const mainFlowWithUpdatedSubflowRefs = produce(mainFlow, (draftMainFlow) => {
    draftMainFlow.actions = draftMainFlow.actions.map((action) => {
      if (!isSubflowReferenceNode(subflow.id, action)) {
        return action;
      }

      const subflowReferenceNode = new SubflowReferenceNode(action);

      if (allSubflowReturnNodes.length === 0) {
        subflowReferenceNode.deleteAllExits();
        return subflowReferenceNode.getReferenceNode();
      }

      allSubflowReturnNodes.forEach((returnToMainFlowNode, _, self) =>
        syncSubflowReferencPayloadWithSubflowReturnNode(
          subflowReferenceNode,
          returnToMainFlowNode,
          self,
        ),
      );

      return subflowReferenceNode.getReferenceNode();
    });
  });

  return mainFlowWithUpdatedSubflowRefs;
}

function getAllCurrentSubflowReturnNodes(
  subflowActions: SubflowMessengerAction[],
): FlowMessengerNodeSubflowReturn[] {
  return (
    subflowActions.filter(
      (action): action is FlowMessengerNodeSubflowReturn =>
        action.nodeType === FlowNodeTypes.SUBFLOW_RETURN,
    ) ?? []
  );
}

function isSubflowReferenceNode(
  subflowId: string,
  action: FlowMessengerAction,
): action is FlowMessengerNodeSubflowReference {
  return (
    action.nodeType === FlowNodeTypes.SUBFLOW_REFERENCE &&
    action.payload?.subflowId === subflowId
  );
}

function syncSubflowReferencPayloadWithSubflowReturnNode(
  subflowReferenceNode: SubflowReferenceNode,
  returnNode: FlowMessengerNodeSubflowReturn,
  allSubflowReturnNodes: FlowMessengerNodeSubflowReturn[],
) {
  const returnNodeIdentifier = returnNode.payload.returnId;

  const exitAlreadyExists =
    subflowReferenceNode.hasPortWithReturnId(returnNodeIdentifier);

  // If the exit does not exist, create a new one and set it to null since we don't know what it will connect to it yet
  if (!exitAlreadyExists) {
    subflowReferenceNode.createNewExitForReturnId(returnNodeIdentifier);
  }

  // If there's an exit that is no longer in the subflow, remove it
  subflowReferenceNode.getExitIdsList().forEach((exitId) => {
    const exitExistsInSubflow = allSubflowReturnNodes.some(
      ({ payload }) => payload.returnId === exitId,
    );

    if (!exitExistsInSubflow) {
      subflowReferenceNode.deleteExitById(exitId);
    }
  });
}
